<script>
    // Licensed under Gnu Public License version 2
    // Copyright (c) 2023 (original work) Open Assessment Technologies SA ;

    /**
     * Defines an option for RadioSwitch.
     * @typedef {object} radioOptionDescriptor
     * @property {*} value - Actual value of the option
     * @property {string} label - Label to display
     * @property {string} ariaLabel - ariaLabel for the option
     */

    /**
     * @param {string} name - name of the radio group
     * @param {*} value - value of currently selected option
     * @param {radioOptionDescriptor[]} options - array of available options
     * @param {boolean} disabled - disabled state
     * @fires change - fires when value has been changed
     */

    import { createEventDispatcher } from 'svelte';
    import { generateElementId } from '@oat-sa-private/ui-core';

    export let name;
    export let options;
    export let value;
    export let disabled = false;

    $: {
        name = name || generateElementId('radioSwitch');
    }

    $: {
        value = value || (options && options.length && options[0].value);
    }
    const dispatch = createEventDispatcher();

    /**
     * Handle change of switch value
     */
    function handleChange() {
        dispatch('change', {
            value
        });
    }
</script>

<style>
    .radioSwitch {
        display: inline-flex;
        align-items: stretch;
        border-radius: var(--radius-xlarge);
        height: 3rem;
        background-color: var(--color-bg-actionable-alternative-inverted);
        border: var(--border-medium) solid var(--color-bg-actionable-alternative-inverted);
        position: relative;
        cursor: pointer;

        &.disabled {
            border-color: var(--color-border-disabled);
            background-color: var(--color-border-disabled);
            cursor: not-allowed;
            & label {
                background-color: var(--color-border-disabled);
                cursor: not-allowed;
                &.checked {
                    color: var(--color-text-disabled);
                }
            }
        }

        & .tabfocus-indicator {
            position: absolute;

            width: 100%;
            height: 100%;
            z-index: var(--layer-1);
            border-radius: var(--radius-xlarge);
            pointer-events: none;
        }

        & label {
            border-radius: 1.5rem;
            font-size: 1.5rem;
            line-height: 2rem;
            font-weight: 700;
            padding: 0.25rem 0.625rem;
            text-transform: uppercase;
            background-color: var(--color-bg-actionable-alternative-inverted);
            color: var(--color-text-actionable-alternative-inverted);
            user-select: none;
            cursor: pointer;
            position: relative;

            &.checked {
                background-color: var(--color-bg-default);
                color: var(--color-bg-actionable-alternative-inverted);
            }

            &:before {
                content: '';
                position: absolute;
                inset-inline-start: 0;
                top: calc(var(--space-1x) * -1);
                height: calc(100% + var(--space-1x) * 2);
                width: 100%;
                z-index: var(--layer-1);
            }

            &:first-of-type {
                &:before {
                    inset-inline-start: calc(var(--space-1x) * -1);
                    width: calc(100% + var(--space-1x));
                }
            }
            &:last-of-type {
                &:before {
                    width: calc(100% + var(--space-1x));
                }
            }
        }

        & input[type='radio'] {
            @add-mixin visually-hidden;

            &:focus-visible:not(.disabled) ~ .tabfocus-indicator {
                @add-mixin simple-outline;
            }
        }
    }
</style>

<span class="radioSwitch" role="radiogroup" class:disabled>
    {#each options as option (option.value)}
        <input
            type="radio"
            {name}
            {disabled}
            class:focus-visible={false}
            id={`${name}-${option.value}`}
            value={option.value}
            aria-label={option.ariaLabel}
            bind:group={value}
            on:change={handleChange} />
        <label class:checked={option.value === value} for={`${name}-${option.value}`}>
            {option.label}
        </label>
    {/each}
    <span class="tabfocus-indicator" />
</span>
